import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useEffect, useState } from "react";
import { formatCpf, formatCnpj, formatTelefone, formatCep } from "./masks.js";
function FieldShell({ label, hint, error, required, children }) {
    return (_jsxs("label", { style: { display: "flex", flexDirection: "column", gap: 6 }, children: [_jsx("span", { style: { fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", color: "var(--text-muted)", textTransform: "uppercase", lineHeight: 1.15, minHeight: "2.3em", display: "flex", alignItems: "flex-start" }, children: _jsxs("span", { children: [label, required ? _jsx("span", { style: { color: "var(--danger-500)", marginLeft: 4 }, children: "*" }) : null] }) }), children, hint && !error ? _jsx("span", { style: { fontSize: 11, color: "var(--text-dim)" }, children: hint }) : null, error ? _jsx("span", { style: { fontSize: 11, color: "var(--danger-500)" }, children: error }) : null] }));
}
const inputStyle = (error) => ({
    background: "var(--bg-elev)",
    border: `1px solid ${error ? "var(--danger-500)" : "var(--border-strong)"}`,
    borderRadius: 10,
    padding: "10px 14px",
    color: "var(--text)",
    fontSize: 14,
    outline: "none",
    fontFamily: "inherit",
    width: "100%",
});
export const TextField = forwardRef(function TextField({ label, hint, error, required, style, ...rest }, ref) {
    return (_jsx(FieldShell, { label: label, hint: hint, error: error, required: required, children: _jsx("input", { ref: ref, ...rest, style: { ...inputStyle(error), ...style } }) }));
});
/** Campo de senha com toggle de mostrar/ocultar. Usa o mesmo FieldShell do
 *  TextField (label reserva 2 linhas), entao alinha perfeito ao lado de outros
 *  campos num FormGrid. Substitui as copias locais que divergiam no layout. */
export function PasswordField({ label, value, onChange, placeholder, hint, error, required, autoComplete, name, }) {
    const [show, setShow] = useState(false);
    return (_jsx(FieldShell, { label: label, hint: hint, error: error, required: required, children: _jsxs("div", { style: { position: "relative" }, children: [_jsx("input", { type: show ? "text" : "password", value: value, onChange: (e) => onChange(e.target.value), placeholder: placeholder, required: required, autoComplete: autoComplete, name: name, style: { ...inputStyle(error), paddingRight: 44 } }), _jsx("button", { type: "button", onClick: () => setShow((s) => !s), "aria-label": show ? "Ocultar senha" : "Mostrar senha", title: show ? "Ocultar senha" : "Mostrar senha", style: {
                        position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)",
                        width: 32, height: 32, borderRadius: 6, border: "none", background: "transparent",
                        color: "var(--text-muted)", cursor: "pointer", display: "flex",
                        alignItems: "center", justifyContent: "center", fontSize: 16,
                    }, children: show ? "🙈" : "👁" })] }) }));
}
export const CurrencyField = forwardRef(function CurrencyField({ label, hint, error, required, value, onValueChange, style, ...rest }, ref) {
    const display = value == null ? "" : new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
    return (_jsx(FieldShell, { label: label, hint: hint, error: error, required: required, children: _jsxs("div", { style: { position: "relative" }, children: [_jsx("span", { style: { position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "var(--text-dim)", fontSize: 13 }, children: "R$" }), _jsx("input", { ref: ref, inputMode: "decimal", type: "text", value: display, onChange: (e) => {
                        const digits = e.target.value.replace(/\D/g, "");
                        if (!digits)
                            return onValueChange(null);
                        onValueChange(Number(digits) / 100);
                    }, ...rest, style: { ...inputStyle(error), paddingLeft: 36, ...style } })] }) }));
});
/**
 * `<input type="number">` que PODE ficar vazio enquanto o usuario digita, sem o
 * shell de label. Exportado pra telas que ja tem rotulo proprio (ex.: os `<Fld>`
 * das telas de API) usarem o mesmo comportamento sem duplicar label.
 *
 * Antes o input era controlado direto pelo numero do pai. Como quase todo
 * chamador faz `onChange={(e) => setX(Number(e.target.value))}`, apagar o campo
 * virava `Number("") === 0` — o campo voltava a mostrar "0" sozinho, e esse zero
 * ainda grudava na frente do proximo digito ("06" em vez de "6"). Cliente
 * reportou 05/08/2026 no campo de juros.
 *
 * Agora o texto digitado e' a fonte da verdade durante a edicao: vazio continua
 * vazio, e zero a esquerda e' removido. O contrato com o pai nao muda — ele
 * recebe o mesmo evento onChange, so que ja com o texto normalizado.
 *
 * ATENCAO pra quem usa: com o campo vazio o PAI fica com 0 (foi ele que
 * converteu). Se 0 nao for um valor valido no seu form, bloqueie o submit —
 * foi assim que a contra-proposta quase gravou contrato com juros zero.
 */
export const NumberInput = forwardRef(function NumberInput({ value, onChange, ...rest }, ref) {
    const [text, setText] = useState(value == null ? "" : String(value));
    // Re-sincroniza quando o valor do pai muda de VERDADE (ex.: o botao
    // "Ajustar ao maximo" preenche o campo). A comparacao e' numerica de
    // proposito: se o pai converteu o vazio em 0, o buffer vazio continua
    // vazio em vez de voltar a "0".
    useEffect(() => {
        const propNum = value === "" || value == null ? NaN : Number(value);
        const bufNum = text.trim() === "" ? NaN : Number(text);
        if (Number.isNaN(propNum) && Number.isNaN(bufNum))
            return;
        if (propNum === bufNum)
            return;
        if (Number.isNaN(bufNum) && propNum === 0)
            return; // acabou de apagar
        setText(value == null ? "" : String(value));
        // `text` fora das deps de proposito: reagir a ele reescreveria o que o
        // usuario esta digitando.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);
    return (_jsx("input", { ref: ref, type: "number", ...rest, value: text, onChange: (e) => {
            // "06" -> "6". Só mexe quando ha digito depois do zero, pra nao
            // atrapalhar quem esta digitando "0,5".
            const limpo = /^-?0\d/.test(e.target.value)
                ? e.target.value.replace(/^(-?)0+/, "$1")
                : e.target.value;
            setText(limpo);
            if (limpo !== e.target.value)
                e.target.value = limpo;
            onChange?.(e);
        } }));
});
export const NumberField = forwardRef(function NumberField({ label, hint, error, required, style, ...rest }, ref) {
    return (_jsx(FieldShell, { label: label, hint: hint, error: error, required: required, children: _jsx(NumberInput, { ref: ref, ...rest, style: { ...inputStyle(error), ...style } }) }));
});
function makeMaskedField(fmt, defaultPlaceholder, maxLen) {
    return forwardRef(function MaskedField({ label, hint, error, required, style, value, onChange, placeholder, ...rest }, ref) {
        const display = fmt(value ?? "");
        return (_jsx(FieldShell, { label: label, hint: hint, error: error, required: required, children: _jsx("input", { ref: ref, type: "text", inputMode: "numeric", autoComplete: "off", maxLength: maxLen, placeholder: placeholder ?? defaultPlaceholder, value: display, onChange: (e) => {
                    // Reescreve o value pra manter só o formato mascarado — evita
                    // usuario colar lixo (letras, virgulas, etc.).
                    const masked = fmt(e.target.value);
                    const synthetic = { ...e, target: { ...e.target, value: masked } };
                    onChange(synthetic);
                }, ...rest, style: { ...inputStyle(error), ...style } }) }));
    });
}
export const CpfField = makeMaskedField(formatCpf, "000.000.000-00", 14);
export const CnpjField = makeMaskedField(formatCnpj, "00.000.000/0000-00", 18);
export const TelefoneField = makeMaskedField(formatTelefone, "(00) 00000-0000", 15);
export const CepField = makeMaskedField(formatCep, "00000-000", 9);
/** Agrupa as opções consecutivas de mesmo `group`, na ordem em que vieram. */
function blocosDeOpcoes(options) {
    const blocos = [];
    for (const o of options) {
        const ultimo = blocos[blocos.length - 1];
        if (ultimo && o.group !== undefined && ultimo.group === o.group)
            ultimo.options.push(o);
        else
            blocos.push({ group: o.group, options: [o] });
    }
    return blocos;
}
export const SelectField = forwardRef(function SelectField({ label, hint, error, required, options, style, ...rest }, ref) {
    const opcao = (o) => (_jsx("option", { value: o.value, children: o.label }, o.value));
    return (_jsx(FieldShell, { label: label, hint: hint, error: error, required: required, children: _jsx("select", { ref: ref, ...rest, style: { ...inputStyle(error), ...style }, children: blocosDeOpcoes(options).map((b, i) => b.group === undefined ? (b.options.map(opcao)) : (_jsx("optgroup", { label: b.group, children: b.options.map(opcao) }, `${b.group}-${i}`))) }) }));
});
export const TextareaField = forwardRef(function TextareaField({ label, hint, error, required, style, ...rest }, ref) {
    return (_jsx(FieldShell, { label: label, hint: hint, error: error, required: required, children: _jsx("textarea", { ref: ref, ...rest, style: { ...inputStyle(error), minHeight: 80, resize: "vertical", ...style } }) }));
});
export function FormGrid({ cols = 2, children }) {
    return (_jsx("div", { style: {
            display: "grid",
            gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
            gap: 16,
        }, children: children }));
}
export function FormActions({ children }) {
    return (_jsx("div", { style: { display: "flex", gap: 12, justifyContent: "flex-end", marginTop: 8, flexWrap: "wrap" }, children: children }));
}
//# sourceMappingURL=FormField.js.map